import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { devLogin, errorMessage, getAuthConfig, type AuthConfig, type User } from './api';
import { Logo } from './Logo';

type Props = {
  onLoggedIn: (user: User) => void;
};

function loginErrorMessage(code: string): string {
  if (code === 'state') {
    return '로그인 요청이 만료됐거나 어긋났다. 다시 눌러라.';
  }
  return '로그인하지 못했다. 다시 시도해 달라.';
}

function peekLoginError(): string | null {
  const code = new URLSearchParams(window.location.search).get('login_error');
  return code ? loginErrorMessage(code) : null;
}

function stripLoginErrorFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('login_error')) return;
  params.delete('login_error');
  const qs = params.toString();
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', next);
}

export function LoginPage({ onLoggedIn }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(peekLoginError);
  const [config, setConfig] = useState<AuthConfig | null>(null);

  useEffect(() => {
    stripLoginErrorFromUrl();
    let cancelled = false;
    void (async () => {
      try {
        const next = await getAuthConfig();
        if (!cancelled) setConfig(next);
      } catch (err) {
        if (!cancelled) {
          setError(`로그인 설정을 불러오지 못했다: ${errorMessage(err)}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleDevLogin() {
    setBusy(true);
    setError(null);
    try {
      const user = await devLogin();
      onLoggedIn(user);
    } catch (err) {
      setError(`로그인에 실패했다: ${errorMessage(err)}`);
      setBusy(false);
    }
  }

  function handleGoogle() {
    window.location.href = '/api/auth/google/start';
  }

  const busyLabel = busy ? '로그인 중…' : null;

  return (
    /*
      **바탕에 브랜드 기운을 한 겹 깐다**(2026-08-29 지시: 「세련되게
      컴팩트하게」). 옛 판은 완전 단색 회색이라 카드가 떠 있는 것이 아니라
      **떨어져 있는** 것으로 보였다 — 화면에 요소가 하나뿐일 때 단색 바탕은
      절제가 아니라 미완성으로 읽힌다.

      ⚠ **색을 새로 안 만든다.** 브랜드색의 알파로 파생한다(`color-mix`) —
      테마 일곱이 각자 제 기운을 입고, 어두운 다섯에서도 따로 손댈 것이 없다.
    */
    <main
      className="grid min-h-full place-items-center bg-canvas p-s4"
      style={{
        // ⚠ **카드 뒤에 둔다.** 처음에 `at 50% 0%` 로 화면 꼭대기에 뒀더니
        // 기운이 카드와 떨어진 곳에서 혼자 빛나 **카드가 더 외로워 보였다**
        // (실측). 빛은 주인공 뒤에서 나와야 주인공을 세운다.
        backgroundImage:
          'radial-gradient(52% 44% at 50% 46%,' +
          ' color-mix(in srgb, var(--brand) 12%, transparent), transparent 72%)',
      }}
    >
      {/*
        **380 → 340px**(2026-08-29). 안에 든 것이 심볼·이름·단추 셋뿐이라
        380 은 단추가 가로로 늘어져 글자가 가운데 작게 떠 보였다.
      */}
      <div className="w-[340px] max-w-full">
        {/*
          로그인은 **가운데로 모은다.** 화면에 이 카드 하나뿐이라 왼쪽 정렬은
          기준선이 될 것이 없어 떠 보인다. 심볼을 세워 눈이 먼저 닿게 하고,
          제품명과 단추가 그 아래로 내려간다.
        */}
        <div className="rounded-lg border border-line bg-panel px-s5 pt-s6 pb-s5 text-center shadow-lift">
          {/* 비율은 viewBox(29×24)를 지킨다 — 한쪽만 키우면 심볼이 눌린다.
              72×60 → 52×43: 카드가 좁아진 만큼 심볼도 줄인다. */}
          <Logo width={52} height={43} />
          {/* **설명 문구를 두지 않는다.** 로그인 화면에서 할 수 있는 일이
              하나뿐이라 설명이 단추와 눈을 다툰다. */}
          <h1 className="mt-s3 mb-s5">
            <span className="wordmark text-lead">PLUSME OFFICE</span>
          </h1>
          {config?.google && (
            <Button
              type="button"
              block
              className="btn-press"
              onClick={handleGoogle}
              disabled={busy}
            >
              {/* ⚠ **흰 판 없이 단추 위에 바로 얹는다**(2026-08-26 지시).
                  구글 심볼의 파랑(#4285F4)이 단추 파랑과 가까워 그 획만
                  묻히는데, 빨강·노랑·초록이 남아 형태는 읽힌다. 되돌리려면
                  `bg-white` 판을 다시 깔아라. */}
              <GoogleMark size={18} />
              {busyLabel ?? 'Google 계정으로 로그인'}
            </Button>
          )}
          {config?.dev_login && (
            <Button
              type="button"
              block
              variant={config.google ? 'ghost' : 'default'}
              className={config.google ? 'btn-press mt-s2' : 'btn-press'}
              onClick={() => void handleDevLogin()}
              disabled={busy}
            >
              {/* **개발용 로그인도 같은 모양이다.** 로컬에서는 이 단추
                  하나만 보이고, 그 자리는 서버에서 구글 로그인이 서는
                  자리다 — 모양이 다르면 로컬에서 본 화면과 서버 화면이
                  달라진다. 둘이 함께 뜨는 것은 로컬에서 구글 설정까지
                  켰을 때뿐이고, 그때는 아래쪽이 ghost 라 구분된다. */}
              <GoogleMark size={18} />
              {busyLabel ?? (config.google ? '개발용 로그인' : 'Google 계정으로 로그인')}
            </Button>
          )}
          {/* **설정을 읽는 동안과 「방법이 없다」가 같아 보였다.** 둘 다 단추
              없는 빈 카드였고, 그 상태에서 사람은 무엇을 기다려야 하는지
              알 수 없었다.

              ⚠ 옛 판은 `.auth-note` 클래스를 썼는데 **그 규칙이 CSS 에
              없었다** — 재구축 때 사라진 이름이라 글자가 아무 꼴 없이 떴다. */}
          {config === null && error === null && (
            <p className="m-0 text-meta text-ink-3">로그인 방법을 확인하는 중…</p>
          )}
          {config && !config.google && !config.dev_login && (
            <p className="m-0 text-meta text-ink-3">쓸 수 있는 로그인 방법 없음</p>
          )}
          {error && (
            <div className="mt-s4 rounded-md border border-line bg-canvas p-s3 text-left">
              <Badge tone="red">로그인 실패</Badge>
              <p className="mt-s2 mb-0 text-meta text-ink-2 text-pretty">{error}</p>
            </div>
          )}
        </div>

        {/* ⚠ **카드 아래에 도메인을 적지 마라.** 한 번 넣었다가 걷었다 —
            `office.viewster.io` 를 박으면 로컬에서 거짓이 되고,
            `location.host` 로 바꿔도 가짜 사이트는 자기 host 를 적으면
            그만이라 피싱 방지에 아무 값을 안 한다. 값 없는 글자를 화면에
            두면 그만큼 카드가 덜 선다. */}
      </div>
    </main>
  );
}

function GoogleMark({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.258h2.909c1.702-1.567 2.683-3.874 2.683-6.614z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.181l-2.909-2.258c-.806.54-1.837.859-3.047.859-2.344 0-4.328-1.583-5.036-3.71H.957v2.332C2.438 15.983 5.482 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.346l2.582-2.581C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
